import { useState } from 'react';
import axios from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPencil,
  faTrash,
  faFlask,
  faStar,
  faToggleOn,
  faToggleOff,
  faSearch,
  faSpinner,
  faEnvelope,
  faServer,
  faCheckCircle,
  faTimesCircle,
  faExclamationCircle,
  faChevronLeft,
  faCloud,
  faBolt,
  faPaperPlane,
  faGear,
  faMobileScreen,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  emailGatewayService,
  PROVIDERS,
  GATEWAY_TYPES,
  SMTP_ENCRYPTIONS,
  type EmailGateway,
  type CreateEmailGatewayPayload,
  type Provider,
  type GatewayType,
  type SmtpEncryption,
  type TestEmailResult,
  type EmailGatewayServiceApi,
} from '@/services/email-gateway.service';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/shared/FormField';
import { toast, swalConfirm } from '@/lib/swal';
import { ROUTES } from '@/routes/routes';

// ── Utilities ─────────────────────────────────────────────────────────────────

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

function formatRelative(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// ── Constants ─────────────────────────────────────────────────────────────────

const SELECT_CLASS =
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

const PROVIDER_LABELS: Record<string, string> = {
  SMTP: 'SMTP',
  MAILTRAP: 'Mailtrap',
  BREVO: 'Brevo',
  SES: 'Amazon SES',
  MSG91: 'MSG91',
  SENDGRID: 'Twilio SendGrid',
  CUSTOM: 'Custom',
};

type FAIcon = typeof faServer;

const PROVIDER_ICONS: Record<string, { icon: FAIcon; colorClass: string }> = {
  SMTP: { icon: faServer, colorClass: 'text-blue-600 dark:text-blue-400' },
  MAILTRAP: { icon: faFlask, colorClass: 'text-teal-600 dark:text-teal-400' },
  BREVO: { icon: faBolt, colorClass: 'text-green-600 dark:text-green-400' },
  SES: { icon: faCloud, colorClass: 'text-orange-500 dark:text-orange-400' },
  MSG91: { icon: faMobileScreen, colorClass: 'text-purple-600 dark:text-purple-400' },
  SENDGRID: { icon: faPaperPlane, colorClass: 'text-cyan-600 dark:text-cyan-400' },
  CUSTOM: { icon: faGear, colorClass: 'text-muted-foreground' },
};

const PROVIDER_DEFAULT_TYPE: Record<string, GatewayType> = {
  SMTP: 'SMTP',
  MAILTRAP: 'SMTP',
  BREVO: 'API',
  SES: 'API',
  MSG91: 'API',
  SENDGRID: 'API',
  CUSTOM: 'SMTP',
};

const USAGE_AREAS = [
  { area: 'Email Templates', count: '0 Templates' },
  { area: 'Notifications', count: '0 Workflows' },
  { area: 'Reports', count: '0 Reports' },
  { area: 'System Emails', count: '0 Messages' },
] as const;

// ── Badges & Cells ────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
        status === 'ENABLED'
          ? 'bg-green-500/10 text-green-700 dark:text-green-400'
          : 'bg-muted text-muted-foreground'
      }`}
    >
      {status === 'ENABLED' ? 'Enabled' : 'Disabled'}
    </span>
  );
}

function TypeBadge({ type }: { type: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
      <FontAwesomeIcon icon={type === 'SMTP' ? faServer : faEnvelope} className="h-2.5 w-2.5" />
      {type}
    </span>
  );
}

function HealthBadge({ status }: { status: string }) {
  const cfg: Record<string, { label: string; className: string; icon: FAIcon }> = {
    HEALTHY: {
      label: 'Healthy',
      className: 'bg-green-500/10 text-green-700 dark:text-green-400',
      icon: faCheckCircle,
    },
    WARNING: {
      label: 'Warning',
      className: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
      icon: faExclamationCircle,
    },
    ERROR: {
      label: 'Error',
      className: 'bg-red-500/10 text-red-700 dark:text-red-400',
      icon: faTimesCircle,
    },
    UNKNOWN: {
      label: 'Unknown',
      className: 'bg-muted text-muted-foreground',
      icon: faExclamationCircle,
    },
  };
  const c = cfg[status] ?? cfg.UNKNOWN;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${c.className}`}
    >
      <FontAwesomeIcon icon={c.icon} className="h-2.5 w-2.5" />
      {c.label}
    </span>
  );
}

function PriorityBadge({ priority }: { priority: number }) {
  if (priority === 1) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
        <FontAwesomeIcon icon={faStar} className="h-2.5 w-2.5" />
        Primary
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
      #{priority}
    </span>
  );
}

function ProviderCell({ provider }: { provider: string }) {
  const pi = PROVIDER_ICONS[provider] ?? { icon: faEnvelope, colorClass: 'text-muted-foreground' };
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-muted/50">
        <FontAwesomeIcon icon={pi.icon} className={`h-3 w-3 ${pi.colorClass}`} />
      </div>
      <span className="text-muted-foreground">{PROVIDER_LABELS[provider] ?? provider}</span>
    </div>
  );
}

// ── Diagnostic row for test results ──────────────────────────────────────────

function DiagnosticRow({ label, status }: { label: string; status: string }) {
  const statusCfg: Record<string, { label: string; colorClass: string; icon: FAIcon }> = {
    CONNECTED: {
      label: 'Successful',
      colorClass: 'text-green-700 dark:text-green-400',
      icon: faCheckCircle,
    },
    AUTHENTICATED: {
      label: 'Authenticated',
      colorClass: 'text-green-700 dark:text-green-400',
      icon: faCheckCircle,
    },
    FAILED: { label: 'Failed', colorClass: 'text-red-700 dark:text-red-400', icon: faTimesCircle },
    TIMEOUT: {
      label: 'Timed Out',
      colorClass: 'text-red-700 dark:text-red-400',
      icon: faTimesCircle,
    },
    NOT_TESTED: {
      label: 'Not Tested',
      colorClass: 'text-muted-foreground',
      icon: faExclamationCircle,
    },
    // Send Request states. "Accepted" deliberately stops short of "Delivered".
    ACCEPTED: {
      label: 'Accepted',
      colorClass: 'text-green-700 dark:text-green-400',
      icon: faCheckCircle,
    },
    NOT_ATTEMPTED: {
      label: 'Not Attempted',
      colorClass: 'text-muted-foreground',
      icon: faExclamationCircle,
    },
  };
  const cfg = statusCfg[status] ?? {
    label: status,
    colorClass: 'text-muted-foreground',
    icon: faExclamationCircle,
  };
  return (
    <div className="flex items-center justify-between px-3 py-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`flex items-center gap-1 text-xs font-medium ${cfg.colorClass}`}>
        <FontAwesomeIcon icon={cfg.icon} className="h-3 w-3" />
        {cfg.label}
      </span>
    </div>
  );
}

// ── Inline field error ────────────────────────────────────────────────────────

function FieldError({ msg }: { msg: string | undefined }) {
  if (!msg) return null;
  return <p className="mt-1 text-xs text-red-600 dark:text-red-400">{msg}</p>;
}

// ── Form validation ───────────────────────────────────────────────────────────

interface FormErrors {
  name?: string;
  smtpHost?: string;
  smtpPort?: string;
  smtpUsername?: string;
  smtpPassword?: string;
  apiEndpoint?: string;
  apiKey?: string;
  senderName?: string;
  senderEmail?: string;
  replyToEmail?: string;
  priority?: string;
}

function validateForm(f: FormState, isEdit: boolean): FormErrors {
  const errs: FormErrors = {};

  if (!f.name.trim()) errs.name = 'Gateway name is required';

  const priority = parseInt(f.priority, 10);
  if (isNaN(priority) || priority < 1 || priority > 100) {
    errs.priority = 'Priority must be between 1 and 100';
  }

  if (f.gatewayType === 'SMTP') {
    if (!f.smtpHost.trim()) errs.smtpHost = 'SMTP host is required';
    const port = parseInt(f.smtpPort, 10);
    if (isNaN(port) || port < 1 || port > 65535) {
      errs.smtpPort = 'Port must be between 1 and 65535';
    }
    if (!f.smtpUsername.trim()) errs.smtpUsername = 'Username is required';
    if (!isEdit && !f.smtpPassword) errs.smtpPassword = 'Password is required';
  } else {
    if (!f.apiEndpoint.trim()) {
      errs.apiEndpoint = 'API endpoint is required';
    } else if (!isValidUrl(f.apiEndpoint)) {
      errs.apiEndpoint = 'Must be a valid URL (e.g. https://api.example.com)';
    }
    if (!isEdit && !f.apiKey) errs.apiKey = 'API key is required';
    if (!f.senderName.trim()) errs.senderName = 'Sender name is required';
    if (!f.senderEmail.trim()) {
      errs.senderEmail = 'Sender email is required';
    } else if (!isValidEmail(f.senderEmail)) {
      errs.senderEmail = 'Must be a valid email address';
    }
    if (f.replyToEmail && !isValidEmail(f.replyToEmail)) {
      errs.replyToEmail = 'Must be a valid email address';
    }
  }

  return errs;
}

// ── Gateway Form Modal ────────────────────────────────────────────────────────

interface FormState {
  name: string;
  provider: Provider;
  gatewayType: GatewayType;
  description: string;
  smtpHost: string;
  smtpPort: string;
  smtpUsername: string;
  smtpPassword: string;
  smtpEncryption: SmtpEncryption;
  apiEndpoint: string;
  apiKey: string;
  apiSecretKey: string;
  senderName: string;
  senderEmail: string;
  replyToEmail: string;
  status: 'ENABLED' | 'DISABLED';
  isDefault: boolean;
  priority: string;
  fallbackEnabled: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  provider: 'SMTP',
  gatewayType: 'SMTP',
  description: '',
  smtpHost: '',
  smtpPort: '587',
  smtpUsername: '',
  smtpPassword: '',
  smtpEncryption: 'STARTTLS',
  apiEndpoint: '',
  apiKey: '',
  apiSecretKey: '',
  senderName: '',
  senderEmail: '',
  replyToEmail: '',
  status: 'ENABLED',
  isDefault: false,
  priority: '1',
  fallbackEnabled: false,
};

function gatewayToForm(g: EmailGateway): FormState {
  return {
    name: g.name,
    provider: g.provider as Provider,
    gatewayType: g.gatewayType as GatewayType,
    description: g.description ?? '',
    smtpHost: g.smtpHost ?? '',
    smtpPort: g.smtpPort?.toString() ?? '587',
    smtpUsername: g.smtpUsername ?? '',
    smtpPassword: '',
    smtpEncryption: (g.smtpEncryption as SmtpEncryption) ?? 'STARTTLS',
    apiEndpoint: g.apiEndpoint ?? '',
    apiKey: '',
    apiSecretKey: '',
    senderName: g.senderName ?? '',
    senderEmail: g.senderEmail ?? '',
    replyToEmail: g.replyToEmail ?? '',
    status: g.status as 'ENABLED' | 'DISABLED',
    isDefault: g.isDefault,
    priority: g.priority.toString(),
    fallbackEnabled: g.fallbackEnabled,
  };
}

interface GatewayFormModalProps {
  gateway: EmailGateway | null;
  onClose: () => void;
  onSave: (payload: CreateEmailGatewayPayload, id?: string) => void;
  saving: boolean;
}

function GatewayFormModal({ gateway, onClose, onSave, saving }: GatewayFormModalProps) {
  const [form, setForm] = useState<FormState>(gateway ? gatewayToForm(gateway) : EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const isEdit = !!gateway;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    // Clear validation error for this field on change
    if (key in errors) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key as keyof FormErrors];
        return next;
      });
    }
  };

  const handleProviderChange = (provider: Provider) => {
    set('provider', provider);
    set('gatewayType', PROVIDER_DEFAULT_TYPE[provider]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validateForm(form, isEdit);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});

    const payload: CreateEmailGatewayPayload = {
      name: form.name,
      provider: form.provider,
      gatewayType: form.gatewayType,
      description: form.description || undefined,
      status: form.status,
      isDefault: form.isDefault,
      priority: parseInt(form.priority, 10) || 1,
      fallbackEnabled: form.fallbackEnabled,
    };

    if (form.gatewayType === 'SMTP') {
      payload.smtpHost = form.smtpHost;
      payload.smtpPort = parseInt(form.smtpPort, 10) || 587;
      payload.smtpUsername = form.smtpUsername;
      if (form.smtpPassword) payload.smtpPassword = form.smtpPassword;
      payload.smtpEncryption = form.smtpEncryption;
    } else {
      payload.apiEndpoint = form.apiEndpoint;
      if (form.apiKey) payload.apiKey = form.apiKey;
      if (form.apiSecretKey) payload.apiSecretKey = form.apiSecretKey;
      payload.senderName = form.senderName;
      payload.senderEmail = form.senderEmail;
      if (form.replyToEmail) payload.replyToEmail = form.replyToEmail;
    }

    onSave(payload, gateway?.id);
  };

  const providerIcon = PROVIDER_ICONS[form.provider];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-border bg-card shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-sm font-semibold text-foreground">
            {isEdit ? 'Edit Email Gateway' : 'Add Email Gateway'}
          </h2>
          <button
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} noValidate className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
            {/* General */}
            <fieldset className="space-y-4">
              <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                General
              </legend>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <FormField label="Gateway Name *" htmlFor="gw-name">
                    <Input
                      id="gw-name"
                      value={form.name}
                      onChange={(e) => set('name', e.target.value)}
                      placeholder="Primary SMTP"
                      disabled={saving}
                      className={errors.name ? 'border-red-500 focus-visible:ring-red-500' : ''}
                      aria-invalid={!!errors.name}
                    />
                  </FormField>
                  <FieldError msg={errors.name} />
                </div>

                <div>
                  <FormField label="Provider *" htmlFor="gw-provider">
                    <div className="flex items-center gap-2">
                      {providerIcon && (
                        <FontAwesomeIcon
                          icon={providerIcon.icon}
                          className={`h-4 w-4 shrink-0 ${providerIcon.colorClass}`}
                        />
                      )}
                      <select
                        id="gw-provider"
                        value={form.provider}
                        onChange={(e) => handleProviderChange(e.target.value as Provider)}
                        disabled={saving}
                        className={`${SELECT_CLASS} flex-1`}
                        aria-label="Provider"
                      >
                        {PROVIDERS.map((p) => (
                          <option key={p} value={p}>
                            {PROVIDER_LABELS[p]}
                          </option>
                        ))}
                      </select>
                    </div>
                  </FormField>
                </div>

                <FormField label="Gateway Type *" htmlFor="gw-type">
                  <select
                    id="gw-type"
                    value={form.gatewayType}
                    onChange={(e) => set('gatewayType', e.target.value as GatewayType)}
                    disabled={saving}
                    className={SELECT_CLASS}
                    aria-label="Gateway type"
                  >
                    {GATEWAY_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </FormField>

                <FormField label="Description" htmlFor="gw-desc">
                  <Input
                    id="gw-desc"
                    value={form.description}
                    onChange={(e) => set('description', e.target.value)}
                    placeholder="Optional description"
                    disabled={saving}
                  />
                </FormField>
              </div>
            </fieldset>

            {/* SMTP fields */}
            {form.gatewayType === 'SMTP' && (
              <fieldset className="space-y-4 rounded-lg border border-border p-4">
                <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  SMTP Configuration
                </legend>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <FormField label="Host *" htmlFor="gw-smtp-host">
                      <Input
                        id="gw-smtp-host"
                        value={form.smtpHost}
                        onChange={(e) => set('smtpHost', e.target.value)}
                        placeholder="smtp.gmail.com"
                        disabled={saving}
                        className={
                          errors.smtpHost ? 'border-red-500 focus-visible:ring-red-500' : ''
                        }
                      />
                    </FormField>
                    <FieldError msg={errors.smtpHost} />
                  </div>
                  <div>
                    <FormField label="Port *" htmlFor="gw-smtp-port">
                      <Input
                        id="gw-smtp-port"
                        type="number"
                        min={1}
                        max={65535}
                        value={form.smtpPort}
                        onChange={(e) => set('smtpPort', e.target.value)}
                        placeholder="587"
                        disabled={saving}
                        className={
                          errors.smtpPort ? 'border-red-500 focus-visible:ring-red-500' : ''
                        }
                      />
                    </FormField>
                    <FieldError msg={errors.smtpPort} />
                  </div>
                  <div>
                    <FormField label="Username *" htmlFor="gw-smtp-user">
                      <Input
                        id="gw-smtp-user"
                        value={form.smtpUsername}
                        onChange={(e) => set('smtpUsername', e.target.value)}
                        placeholder="noreply@company.com"
                        disabled={saving}
                        className={
                          errors.smtpUsername ? 'border-red-500 focus-visible:ring-red-500' : ''
                        }
                      />
                    </FormField>
                    <FieldError msg={errors.smtpUsername} />
                  </div>
                  <div>
                    <FormField
                      label={isEdit ? 'Password (leave blank to keep existing)' : 'Password *'}
                      htmlFor="gw-smtp-pass"
                    >
                      <Input
                        id="gw-smtp-pass"
                        type="password"
                        value={form.smtpPassword}
                        onChange={(e) => set('smtpPassword', e.target.value)}
                        placeholder={
                          isEdit && gateway?.hasSmtpPassword ? '••••••••' : 'Enter password'
                        }
                        disabled={saving}
                        className={
                          errors.smtpPassword ? 'border-red-500 focus-visible:ring-red-500' : ''
                        }
                      />
                    </FormField>
                    <FieldError msg={errors.smtpPassword} />
                  </div>
                  <div className="sm:col-span-2">
                    <FormField label="Encryption" htmlFor="gw-smtp-enc">
                      <select
                        id="gw-smtp-enc"
                        value={form.smtpEncryption}
                        onChange={(e) => set('smtpEncryption', e.target.value as SmtpEncryption)}
                        disabled={saving}
                        className={SELECT_CLASS}
                        aria-label="Encryption"
                      >
                        {SMTP_ENCRYPTIONS.map((e) => (
                          <option key={e} value={e}>
                            {e}
                          </option>
                        ))}
                      </select>
                    </FormField>
                  </div>
                </div>
              </fieldset>
            )}

            {/* API fields */}
            {form.gatewayType === 'API' && (
              <fieldset className="space-y-4 rounded-lg border border-border p-4">
                <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  API Configuration
                </legend>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <FormField label="API Endpoint *" htmlFor="gw-api-endpoint">
                      <Input
                        id="gw-api-endpoint"
                        value={form.apiEndpoint}
                        onChange={(e) => set('apiEndpoint', e.target.value)}
                        placeholder="https://api.brevo.com/v3/smtp/email"
                        disabled={saving}
                        className={
                          errors.apiEndpoint ? 'border-red-500 focus-visible:ring-red-500' : ''
                        }
                      />
                    </FormField>
                    <FieldError msg={errors.apiEndpoint} />
                  </div>
                  <div>
                    <FormField
                      label={isEdit ? 'API Key (leave blank to keep existing)' : 'API Key *'}
                      htmlFor="gw-api-key"
                    >
                      <Input
                        id="gw-api-key"
                        type="password"
                        value={form.apiKey}
                        onChange={(e) => set('apiKey', e.target.value)}
                        placeholder={isEdit && gateway?.hasApiKey ? '••••••••' : 'Enter API key'}
                        disabled={saving}
                        className={errors.apiKey ? 'border-red-500 focus-visible:ring-red-500' : ''}
                      />
                    </FormField>
                    <FieldError msg={errors.apiKey} />
                  </div>
                  <FormField label="Secret Key" htmlFor="gw-api-secret">
                    <Input
                      id="gw-api-secret"
                      type="password"
                      value={form.apiSecretKey}
                      onChange={(e) => set('apiSecretKey', e.target.value)}
                      placeholder={
                        isEdit && gateway?.hasApiSecretKey ? '••••••••' : 'Optional secret key'
                      }
                      disabled={saving}
                    />
                  </FormField>
                  <div>
                    <FormField label="Sender Name *" htmlFor="gw-sender-name">
                      <Input
                        id="gw-sender-name"
                        value={form.senderName}
                        onChange={(e) => set('senderName', e.target.value)}
                        placeholder="Service Portal"
                        disabled={saving}
                        className={
                          errors.senderName ? 'border-red-500 focus-visible:ring-red-500' : ''
                        }
                      />
                    </FormField>
                    <FieldError msg={errors.senderName} />
                  </div>
                  <div>
                    <FormField label="Sender Email *" htmlFor="gw-sender-email">
                      <Input
                        id="gw-sender-email"
                        type="email"
                        value={form.senderEmail}
                        onChange={(e) => set('senderEmail', e.target.value)}
                        placeholder="noreply@company.com"
                        disabled={saving}
                        className={
                          errors.senderEmail ? 'border-red-500 focus-visible:ring-red-500' : ''
                        }
                      />
                    </FormField>
                    <FieldError msg={errors.senderEmail} />
                  </div>
                  <div className="sm:col-span-2">
                    <FormField label="Reply-To Email" htmlFor="gw-reply-to">
                      <Input
                        id="gw-reply-to"
                        type="email"
                        value={form.replyToEmail}
                        onChange={(e) => set('replyToEmail', e.target.value)}
                        placeholder="support@company.com"
                        disabled={saving}
                        className={
                          errors.replyToEmail ? 'border-red-500 focus-visible:ring-red-500' : ''
                        }
                      />
                    </FormField>
                    <FieldError msg={errors.replyToEmail} />
                  </div>
                </div>
              </fieldset>
            )}

            {/* Settings */}
            <fieldset className="space-y-4">
              <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Settings
              </legend>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField label="Status" htmlFor="gw-status">
                  <select
                    id="gw-status"
                    value={form.status}
                    onChange={(e) => set('status', e.target.value as 'ENABLED' | 'DISABLED')}
                    disabled={saving}
                    className={SELECT_CLASS}
                    aria-label="Status"
                  >
                    <option value="ENABLED">Enabled</option>
                    <option value="DISABLED">Disabled</option>
                  </select>
                </FormField>
                <div>
                  <FormField label="Priority" htmlFor="gw-priority">
                    <Input
                      id="gw-priority"
                      type="number"
                      min={1}
                      max={100}
                      value={form.priority}
                      onChange={(e) => set('priority', e.target.value)}
                      disabled={saving}
                      className={errors.priority ? 'border-red-500 focus-visible:ring-red-500' : ''}
                    />
                  </FormField>
                  <FieldError msg={errors.priority} />
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <label className="flex cursor-pointer items-center gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={form.isDefault}
                    onChange={(e) => set('isDefault', e.target.checked)}
                    disabled={saving}
                    className="h-4 w-4 accent-primary"
                  />
                  <div>
                    <span className="font-medium text-foreground">Set as Default Gateway</span>
                    <p className="text-xs text-muted-foreground">
                      Default gateway is used when no specific gateway is configured for a workflow
                    </p>
                  </div>
                </label>
                <label className="flex cursor-pointer items-center gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={form.fallbackEnabled}
                    onChange={(e) => set('fallbackEnabled', e.target.checked)}
                    disabled={saving}
                    className="h-4 w-4 accent-primary"
                  />
                  <div>
                    <span className="font-medium text-foreground">Enable Fallback</span>
                    <p className="text-xs text-muted-foreground">
                      If this gateway fails, the next priority gateway will be attempted
                    </p>
                  </div>
                </label>
              </div>
            </fieldset>

            {/* Usage */}
            <fieldset className="space-y-3">
              <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Gateway Usage
              </legend>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {USAGE_AREAS.map(({ area, count }) => (
                  <div
                    key={area}
                    className="flex flex-col items-center gap-1.5 rounded-lg border border-dashed border-border bg-muted/20 p-3 text-center"
                  >
                    <span className="text-xs font-medium text-foreground">{area}</span>
                    <span className="text-[11px] font-semibold text-primary">{count}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Assignments are managed within each module (Email Templates, Notifications, etc.).
              </p>
            </fieldset>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
            <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="min-w-[110px]">
              {saving ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />
                  {isEdit ? 'Saving…' : 'Creating…'}
                </>
              ) : isEdit ? (
                'Save Changes'
              ) : (
                'Create Gateway'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Test Email Modal ──────────────────────────────────────────────────────────

interface TestModalProps {
  gateway: EmailGateway;
  onClose: () => void;
  /** Tenant or platform gateway service — the dialog is shared by both. */
  service: EmailGatewayServiceApi;
}

function TestEmailModal({ gateway, onClose, service }: TestModalProps) {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [subject, setSubject] = useState('Test Email from Service Portal');
  const [message, setMessage] = useState(
    'This is a test email to verify your gateway configuration.',
  );
  const [result, setResult] = useState<TestEmailResult | null>(null);
  const [testing, setTesting] = useState(false);

  const handleTest = async (e: React.FormEvent) => {
    e.preventDefault();
    setTesting(true);
    setResult(null);
    try {
      const res = await service.test(gateway.id, { recipientEmail, subject, message });
      setResult(res);
    } catch (err) {
      const msg = axios.isAxiosError(err)
        ? ((err.response?.data as { error?: { message?: string } })?.error?.message ??
          'Test failed')
        : 'Test failed';
      setResult({
        success: false,
        message: msg,
        // Both gateway types now attempt a real send, so the request failing
        // before it reached the provider is FULL_DELIVERY / NOT_ATTEMPTED for
        // either one. This used to claim CONNECTIVITY for API gateways.
        testType: 'FULL_DELIVERY',
        connectionStatus: 'FAILED',
        authStatus: 'NOT_TESTED',
        sendStatus: 'NOT_ATTEMPTED',
        responseTimeMs: 0,
        testedAt: new Date().toISOString(),
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-xl border border-border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Test Gateway</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {gateway.name} · {gateway.gatewayType}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <form
          onSubmit={(e) => {
            void handleTest(e);
          }}
          className="space-y-4 p-6"
        >
          {/*
            This used to read "Connectivity test only — full email delivery
            requires provider-specific SDK integration." That was stale: the API
            path sends through the same provider dispatch production mail uses,
            and no SDK is involved. It now says what actually happens.
          */}
          <div className="flex items-start gap-2 rounded-md bg-primary/5 px-3 py-2.5">
            <FontAwesomeIcon
              icon={faExclamationCircle}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary"
            />
            <p className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">This sends a real email.</span> A
              clearly-marked test message is submitted through this gateway, using the same path the
              application uses for live mail. No business template is used.
            </p>
          </div>

          <FormField label="Recipient Email *" htmlFor="test-email">
            <Input
              id="test-email"
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="admin@example.com"
              required
              disabled={testing}
            />
          </FormField>

          {gateway.gatewayType === 'SMTP' && (
            <>
              <FormField label="Subject" htmlFor="test-subject">
                <Input
                  id="test-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  disabled={testing}
                />
              </FormField>
              <FormField label="Message" htmlFor="test-msg">
                <textarea
                  id="test-msg"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  disabled={testing}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </FormField>
            </>
          )}

          {/* Enhanced result block */}
          {result && (
            <div
              className={`rounded-lg border ${result.success ? 'border-green-500/30 bg-green-500/5' : 'border-red-500/30 bg-red-500/5'}`}
            >
              {/* Status header */}
              <div className="flex items-start gap-2 border-b border-inherit px-4 py-3">
                <FontAwesomeIcon
                  icon={result.success ? faCheckCircle : faTimesCircle}
                  className={`mt-0.5 h-4 w-4 shrink-0 ${result.success ? 'text-green-600' : 'text-red-600'}`}
                />
                <div className="min-w-0">
                  <p
                    className={`text-sm font-semibold ${result.success ? 'text-green-800 dark:text-green-400' : 'text-red-800 dark:text-red-400'}`}
                  >
                    {result.success ? 'Send accepted by provider' : 'Test failed'}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{result.message}</p>
                </div>
              </div>

              {/* Diagnostic rows */}
              <div className="divide-y divide-border/50">
                <DiagnosticRow label="Connection" status={result.connectionStatus} />
                <DiagnosticRow label="Authentication" status={result.authStatus} />
                <DiagnosticRow label="Send Request" status={result.sendStatus} />
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="text-xs text-muted-foreground">Response Time</span>
                  <span className="text-xs font-medium text-foreground">
                    {result.responseTimeMs}ms
                  </span>
                </div>
              </div>

              {/* Endpoint / gateway response */}
              {result.gatewayResponse && (
                <div className="border-t border-inherit px-4 py-3">
                  <p className="mb-1 text-xs font-medium text-muted-foreground">
                    Provider Response
                  </p>
                  <code className="block rounded bg-background/70 px-2 py-1.5 text-xs text-foreground">
                    {result.gatewayResponse}
                  </code>
                </div>
              )}

              {/* Error detail */}
              {result.errorDetail && (
                <div className="border-t border-inherit px-4 py-3">
                  <p className="mb-1 text-xs font-medium text-red-600 dark:text-red-400">
                    Error Details
                  </p>
                  <code className="block break-all rounded bg-red-50 px-2 py-1.5 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-400">
                    {result.errorDetail}
                  </code>
                </div>
              )}

              {/*
                Accepted is not delivered. The provider has taken the message;
                what happens in the recipient's mailbox is asynchronous and this
                product cannot observe it, so the UI must not imply otherwise.
              */}
              {result.sendStatus === 'ACCEPTED' && (
                <div className="border-t border-inherit px-4 py-3">
                  <div className="flex items-start gap-2 rounded-md bg-background/50 px-3 py-2">
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground"
                    />
                    <p className="text-xs text-muted-foreground">
                      The provider accepted the message for delivery. Final arrival in the
                      recipient&apos;s mailbox is asynchronous and is not confirmed here — check the
                      inbox to complete the verification.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <Button type="button" variant="ghost" onClick={onClose} disabled={testing}>
              Close
            </Button>
            <Button type="submit" disabled={testing} className="min-w-[140px]">
              {testing ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />
                  Testing…
                </>
              ) : (
                'Send Test Email'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Empty State ───────────────────────────────────────────────────────────────

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card px-6 py-16 text-center">
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
        <FontAwesomeIcon icon={faServer} className="h-5 w-5 text-primary" />
      </div>
      <p className="mb-1 text-sm font-medium text-foreground">No email gateways configured</p>
      <p className="mb-5 text-xs text-muted-foreground">
        Add your first gateway to enable email delivery for your workspace.
      </p>
      <Button size="sm" onClick={onAdd}>
        <FontAwesomeIcon icon={faPlus} className="mr-2 h-3.5 w-3.5" />
        Add Gateway
      </Button>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export interface EmailGatewayPageProps {
  /**
   * Gateway service to drive the screen. Defaults to the tenant service; the
   * Platform Console passes the platform-bound one so both surfaces share this
   * component rather than duplicating a second Gateway Management page.
   */
  service?: EmailGatewayServiceApi;
  /** Platform scope adds the Backup gateway affordance (resolution step 5). */
  scope?: 'TENANT' | 'PLATFORM';
  /** Isolates react-query caches between the two surfaces. */
  cacheKey?: string;
  title?: string;
  subtitle?: string;
  /** Hidden for the Platform Console, which has its own breadcrumb chrome. */
  showBackLink?: boolean;
}

export function EmailGatewayPage({
  service = emailGatewayService,
  scope = 'TENANT',
  cacheKey = 'email-gateways',
  title,
  subtitle,
  showBackLink = true,
}: EmailGatewayPageProps = {}) {
  const isPlatform = scope === 'PLATFORM';
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [testGateway, setTestGateway] = useState<EmailGateway | null>(null);
  const [editTarget, setEditTarget] = useState<EmailGateway | null>(null);

  const { data: allGateways = [], isLoading } = useQuery({
    queryKey: [cacheKey, search, statusFilter, typeFilter],
    queryFn: () =>
      service.list({
        search: search || undefined,
        status: statusFilter || undefined,
        gatewayType: typeFilter || undefined,
      }),
  });

  // Client-side provider filter (API doesn't have this param)
  const gateways = providerFilter
    ? allGateways.filter((gw) => gw.provider === providerFilter)
    : allGateways;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [cacheKey] });

  const createMutation = useMutation({
    mutationFn: service.create,
    onSuccess: () => {
      toast.success('Gateway created');
      void invalidate();
      setShowForm(false);
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to create gateway')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<CreateEmailGatewayPayload> }) =>
      service.update(id, payload),
    onSuccess: () => {
      toast.success('Gateway updated');
      void invalidate();
      setShowForm(false);
      setEditTarget(null);
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to update gateway')),
  });

  const deleteMutation = useMutation({
    mutationFn: service.remove,
    onSuccess: () => {
      toast.success('Gateway deleted');
      void invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to delete gateway')),
  });

  const enableMutation = useMutation({
    mutationFn: service.enable,
    onSuccess: () => {
      toast.success('Gateway enabled');
      void invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to enable gateway')),
  });

  const disableMutation = useMutation({
    mutationFn: service.disable,
    onSuccess: () => {
      toast.success('Gateway disabled');
      void invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to disable gateway')),
  });

  const defaultMutation = useMutation({
    mutationFn: service.setDefault,
    onSuccess: () => {
      toast.success('Default gateway updated');
      void invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to set default')),
  });

  // Platform only — resolution step 5.
  const backupMutation = useMutation({
    mutationFn: service.setBackup,
    onSuccess: () => {
      toast.success('Platform backup gateway updated');
      void invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to set backup')),
  });

  const handleSave = (payload: CreateEmailGatewayPayload, id?: string) => {
    if (id) {
      updateMutation.mutate({ id, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleToggleStatus = (g: EmailGateway) => {
    if (g.status === 'ENABLED') {
      void handleDisableGateway(g);
    } else {
      enableMutation.mutate(g.id);
    }
  };

  const handleDisableGateway = async (g: EmailGateway) => {
    if (g.isDefault) {
      const ok = await swalConfirm({
        title: 'Disable Default Gateway?',
        text: `"${g.name}" is the current default gateway. Disabling it may interrupt outgoing email delivery.`,
        confirmText: 'Disable Anyway',
        cancelText: 'Cancel',
      });
      if (!ok) return;
    }
    disableMutation.mutate(g.id);
  };

  const handleDelete = async (g: EmailGateway) => {
    const confirmed = await swalConfirm({
      title: g.isDefault ? 'Delete Default Gateway?' : `Delete "${g.name}"?`,
      text: g.isDefault
        ? `"${g.name}" is the current default gateway. Deleting it may interrupt outgoing email delivery. This cannot be undone.`
        : 'This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });
    if (confirmed) deleteMutation.mutate(g.id);
  };

  const handleOpenForm = (gateway: EmailGateway | null) => {
    setEditTarget(gateway);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditTarget(null);
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-5">
      {/* Breadcrumb — tenant portal only; the Platform Console has its own chrome */}
      {showBackLink && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Link
            to={ROUTES.SETTINGS_COMMUNICATION}
            className="flex items-center gap-1.5 transition-colors hover:text-foreground"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="h-2.5 w-2.5" />
            Communication
          </Link>
          <span>/</span>
          <span className="text-foreground">Email Gateway</span>
        </div>
      )}

      <PageHeader
        title={title ?? 'Email Gateway'}
        description={
          subtitle ??
          'Configure SMTP and API email providers. Set priorities and fallback routing for reliable email delivery.'
        }
      />

      {isPlatform && (
        <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
          Platform gateways are the fallback for every company. Email is routed in this order:{' '}
          <span className="font-medium text-foreground">
            tenant assigned → tenant default → any enabled tenant gateway → platform default →
            platform backup
          </span>
          . A company that has configured its own gateway is never routed through these.
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[180px] flex-1">
          <FontAwesomeIcon
            icon={faSearch}
            className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search gateways…"
            className="pl-9"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className={`${SELECT_CLASS} w-32`}
          aria-label="Filter by status"
        >
          <option value="">All Status</option>
          <option value="ENABLED">Enabled</option>
          <option value="DISABLED">Disabled</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className={`${SELECT_CLASS} w-28`}
          aria-label="Filter by type"
        >
          <option value="">All Types</option>
          <option value="SMTP">SMTP</option>
          <option value="API">API</option>
        </select>
        <select
          value={providerFilter}
          onChange={(e) => setProviderFilter(e.target.value)}
          className={`${SELECT_CLASS} w-36`}
          aria-label="Filter by provider"
        >
          <option value="">All Providers</option>
          {PROVIDERS.map((p) => (
            <option key={p} value={p}>
              {PROVIDER_LABELS[p]}
            </option>
          ))}
        </select>
        <Button size="sm" onClick={() => handleOpenForm(null)} className="ml-auto gap-2">
          <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5" />
          Add Gateway
        </Button>
      </div>

      {/* Table / Empty */}
      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <FontAwesomeIcon
            icon={faSpinner}
            className="h-5 w-5 animate-spin text-muted-foreground"
          />
        </div>
      ) : gateways.length === 0 ? (
        <EmptyState onAdd={() => handleOpenForm(null)} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Gateway Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Provider
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Health
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Priority
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Default
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Last Tested
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {gateways.map((gw) => (
                  <tr key={gw.id} className="transition-colors hover:bg-muted/30">
                    <td className="px-4 py-3 font-medium text-foreground">{gw.name}</td>
                    <td className="px-4 py-3">
                      <ProviderCell provider={gw.provider} />
                    </td>
                    <td className="px-4 py-3">
                      <TypeBadge type={gw.gatewayType} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={gw.status} />
                    </td>
                    <td className="px-4 py-3">
                      <HealthBadge status={gw.healthStatus} />
                    </td>
                    <td className="px-4 py-3">
                      <PriorityBadge priority={gw.priority} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        {gw.isDefault && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
                            <FontAwesomeIcon icon={faStar} className="h-2.5 w-2.5" />
                            Default
                          </span>
                        )}
                        {isPlatform && gw.isBackup && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/10 px-2 py-0.5 text-[11px] font-medium text-sky-700 dark:text-sky-400">
                            <FontAwesomeIcon icon={faShieldHalved} className="h-2.5 w-2.5" />
                            Backup
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-muted-foreground">
                        {formatRelative(gw.lastTestedAt)}
                      </div>
                      {gw.lastUsedAt && (
                        <div className="mt-0.5 text-[10px] text-muted-foreground/70">
                          used {formatRelative(gw.lastUsedAt)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-0.5">
                        <button
                          onClick={() => handleOpenForm(gw)}
                          title="Edit gateway"
                          aria-label="Edit gateway"
                          className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        >
                          <FontAwesomeIcon icon={faPencil} className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(gw)}
                          title={gw.status === 'ENABLED' ? 'Disable gateway' : 'Enable gateway'}
                          aria-label={
                            gw.status === 'ENABLED' ? 'Disable gateway' : 'Enable gateway'
                          }
                          className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        >
                          <FontAwesomeIcon
                            icon={gw.status === 'ENABLED' ? faToggleOn : faToggleOff}
                            className="h-3.5 w-3.5"
                          />
                        </button>
                        {isPlatform && !gw.isBackup && (
                          <button
                            onClick={() => backupMutation.mutate(gw.id)}
                            title="Set as platform backup gateway"
                            aria-label="Set as platform backup gateway"
                            className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                          >
                            <FontAwesomeIcon icon={faShieldHalved} className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {!gw.isDefault && (
                          <button
                            onClick={() => defaultMutation.mutate(gw.id)}
                            title="Set as default gateway"
                            aria-label="Set as default gateway"
                            className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                          >
                            <FontAwesomeIcon icon={faStar} className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => setTestGateway(gw)}
                          title="Test gateway"
                          aria-label="Test gateway"
                          className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        >
                          <FontAwesomeIcon icon={faFlask} className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => {
                            void handleDelete(gw);
                          }}
                          title={
                            gw.isDefault
                              ? 'Delete default gateway (confirmation required)'
                              : 'Delete gateway'
                          }
                          aria-label="Delete gateway"
                          className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/20"
                        >
                          <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer note */}
          <div className="border-t border-border bg-muted/10 px-4 py-2.5">
            <p className="text-xs text-muted-foreground">
              Gateways are tried in priority order (lowest first). Enable fallback to use automatic
              failover. Health status updates after each test.
            </p>
          </div>
        </div>
      )}

      {/* Modals */}
      {showForm && (
        <GatewayFormModal
          gateway={editTarget}
          onClose={handleCloseForm}
          onSave={handleSave}
          saving={saving}
        />
      )}
      {testGateway && (
        <TestEmailModal
          gateway={testGateway}
          service={service}
          onClose={() => {
            setTestGateway(null);
            void invalidate();
          }}
        />
      )}
    </div>
  );
}
